import { ComponentType, CSSProperties, MouseEvent as ReactMouseEvent, ReactNode, SVGProps, useId, useState } from 'react';
import { styled } from '@mui/material/styles';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import type { TooltipProps } from '@mui/material/Tooltip';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ReactComponent as InfoIcon } from '../../../assets/icons/info-goals-page.svg';
import { Typography } from '../../atoms';
import { Dropdown } from '../../atoms/Dropdown';
import { useDropdownState } from '../../../common/hooks/useDropdownState';
import { PopperPlacementType } from '@mui/material/Popper';
import { TypographyVariant } from '../../../common/types/typography';
import { FEATURE_NAMES } from '../../../common/constants';
import { checkFFEnabled } from '../../../utils';

type Props = {
    width?: number;
    onOpen?(): void;
    placement?: PopperPlacementType;
    message?: ReactNode;
    t?: string;
    style?: CSSProperties;
    children?: ReactNode;
    tProps?: {};
    messageVariant?: TypographyVariant;
    showInfoIcon?: boolean;
    showInfoOnClick?: boolean;
    bordered?: boolean;
    icon?: ComponentType<SVGProps<SVGSVGElement>>;
    /** Pixel size for the `showInfoIcon` glyph (defaults to the 20px `.info-icon` slot). */
    infoIconSize?: number;
    /** When set, the tooltip is controlled (no hover) and stays open/closed per this flag. */
    open?: boolean;
};

/**
 *
 * @param message
 * @param onOpen a callback called every time the dropdown opens
 * @param placement the position of the modal
 * @returns
 */
export const InfoTooltip = ({
    onOpen,
    width,
    placement = 'right-start',
    message,
    style,
    t,
    tProps,
    children,
    messageVariant = 'labels.smallDemibold',
    showInfoIcon = false,
    showInfoOnClick = false,
    bordered = false,
    icon: Icon = InfoIcon,
    infoIconSize,
    open
}: Props) => {
    const uniqId = useId();
    const isNewTooltip = checkFFEnabled(FEATURE_NAMES['NEW_TOOLTIP_FTDS_AEX-3428']);
    // Optional explicit size for the standalone info glyph, overriding the 20px `.info-icon` slot.
    const infoIconStyle = infoIconSize ? { width: infoIconSize, height: infoIconSize } : undefined;

    const { toggle, isActive, ...dropdownProps } = useDropdownState({ onOpen });
    const [clickOpen, setClickOpen] = useState(false);

    // Click-to-toggle for the info icon. When the tooltip is already open, the
    // Dropdown's ClickAwayListener closes it on mousedown, so we must NOT reopen
    // it here on the trailing click — otherwise the icon could never close it.
    const handleIconClick = (event: ReactMouseEvent<HTMLElement>) => {
        if (showInfoOnClick && isActive) return; // race-guard only in click-only mode
        toggle(event);
    };

    // New FTDS tooltip (AEX-3428): MUI Tooltip with an arrow whose bulge auto-aligns to the
    // trigger for any `placement`. Gated so the legacy Dropdown flow is untouched when off.
    if (isNewTooltip) {
        // Reuse the Typography atom (as the legacy path does) so `t`/`tProps` translations that embed
        // HTML keep rendering; sx pins the FTDS 14px/19px body without adding a new variant token.
        const title = (
            <Typography
                as="div"
                t={t}
                tProps={tProps}
                variant={messageVariant}
                color="semantic.text.default"
                sx={{ fontSize: '14px', fontWeight: 400, lineHeight: '19px', letterSpacing: 0 }}
            >
                {message}
            </Typography>
        );

        const trigger = (
            <>
                {children || (
                    <ButtonIcon>
                        <Icon />
                    </ButtonIcon>
                )}
                {showInfoIcon && (
                    <ButtonIcon className="info-icon">
                        <InfoIcon style={infoIconStyle} />
                    </ButtonIcon>
                )}
            </>
        );

        const slotProps = { popper: { sx: newTooltipSx(width, bordered) } } as const;

        // Popper allows `auto*` placements but MUI Tooltip's arrow variants don't; callers never
        // pass `auto`, so narrow to the Tooltip-supported set.
        const tooltipPlacement = placement as TooltipProps['placement'];

        if (showInfoOnClick) {
            const toggleClick = () =>
                setClickOpen((prev) => {
                    const next = !prev;
                    if (next) onOpen?.();
                    return next;
                });
            return (
                <ClickAwayListener onClickAway={() => setClickOpen(false)}>
                    <Tooltip
                        title={title}
                        placement={tooltipPlacement}
                        arrow
                        open={clickOpen}
                        onClose={() => setClickOpen(false)}
                        disableHoverListener
                        disableFocusListener
                        disableTouchListener
                        slotProps={slotProps}
                    >
                        <div style={style} id={uniqId} onClick={toggleClick}>
                            {trigger}
                        </div>
                    </Tooltip>
                </ClickAwayListener>
            );
        }

        // Controlled mode: caller drives visibility (e.g. show while a field is in error) and
        // hover/focus/touch triggers are disabled so it only reflects the `open` flag.
        if (open !== undefined) {
            return (
                <Tooltip
                    title={title}
                    placement={tooltipPlacement}
                    arrow
                    open={open}
                    onOpen={onOpen}
                    disableHoverListener
                    disableFocusListener
                    disableTouchListener
                    slotProps={slotProps}
                >
                    <div style={style} id={uniqId}>
                        {trigger}
                    </div>
                </Tooltip>
            );
        }

        return (
            <Tooltip title={title} placement={tooltipPlacement} arrow onOpen={onOpen} slotProps={slotProps}>
                <div style={style} id={uniqId}>
                    {trigger}
                </div>
            </Tooltip>
        );
    }

    return (
        <div
            style={style}
            id={uniqId}
            onMouseOver={!showInfoOnClick ? toggle : undefined}
            onMouseLeave={
                !showInfoOnClick
                    ? () => {
                          toggle();
                      }
                    : undefined
            }
        >
            {children || (
                <ButtonIcon onClick={handleIconClick}>
                    <Icon />
                </ButtonIcon>
            )}

            {showInfoIcon && (
                <ButtonIcon onClick={handleIconClick} className="info-icon">
                    <InfoIcon style={infoIconStyle} />
                </ButtonIcon>
            )}

            <Dropdown placement={placement} {...dropdownProps}>
                <Content width={width}>
                    <Typography t={t} tProps={tProps} variant={messageVariant} color="semantic.text.default">
                        {message}
                    </Typography>
                </Content>
            </Dropdown>
        </div>
    );
};

const ButtonIcon = styled('div')(() => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    width: ICON_SIZE + 6,
    height: ICON_SIZE,
    '> svg': {
        zIndex: 1,
        position: 'absolute',
        width: ICON_SIZE,
        height: ICON_SIZE
    },
    '&.info-icon': {
        '> svg': {
            width: '20px',
            height: '20px'
        }
    }
}));

const Content = styled('div')<{
    width?: number;
}>(({ width, theme }) => ({
    width,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 120,
    background: theme.palette.semantic.bg.raised,
    border: `1px solid ${theme.palette.semantic.bd.subtle}`,
    borderRadius: '4px',
    boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.05) !important',
    padding: '20px',
    margin: '8px',
    maxWidth: '100%'
}));

// FTDS tooltip styling (AEX-3428): white card, 12px radius/padding, 14px/19px body text and a
// white arrow. The drop-shadow lives on the popper so it wraps the card AND the arrow bulge as
// one silhouette. MUI positions the arrow against the trigger for every `placement`.
const newTooltipSx =
    (width?: number, bordered?: boolean): SxProps<Theme> =>
    (theme) => ({
        zIndex: theme.zIndex.tooltip,
        // Bordered variant (date-field error tooltips) follows the FTDS spec: a 1px nc1-300 stroke
        // around the white card plus the softer purple drop-shadow.
        filter: bordered
            ? 'drop-shadow(2px 4px 16px rgba(74, 48, 74, 0.12))'
            : 'drop-shadow(2px 4px 8px rgba(0, 0, 0, 0.12))',
        [`& .${tooltipClasses.tooltip}`]: {
            backgroundColor: theme.palette.semantic.bg.default,
            color: theme.palette.semantic.text.default,
            fontFamily: "'TT Commons Pro'",
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '19px',
            letterSpacing: 0,
            borderRadius: '12px',
            padding: '12px',
            maxWidth: width ?? 384,
            boxShadow: 'none',
            wordBreak: 'break-word',
            ...(bordered && { border: `1px solid ${theme.palette.semantic.bd.subtle}` })
        },
        [`& .${tooltipClasses.arrow}`]: {
            color: theme.palette.semantic.bg.default,
            ...(bordered && { '&::before': { border: `1px solid ${theme.palette.semantic.bd.subtle}` } })
        }
    });

const ICON_SIZE = 15;
